import type { Location, PractitionerRole } from "@medplum/fhirtypes";

import type { DayCode } from "./fhir";

export type PractitionerRoleData = {
  role: PractitionerRole;
  location: Location | undefined;
};

export enum CalendarType {
  Google = "google_calendar",
  Huli = "huli_practice",
}

export type TimeRange = { start: string; end: string };

export type DaySchedule = {
  enabled: boolean;
  ranges: TimeRange[];
};

export type WeekSchedule = Record<DayCode, DaySchedule>;

export type SlotBuffer = {
  enabled: boolean;
  minutes: number;
};

/**
 * A preset in a minutes select. `label` is an i18n key.
 */
export type DurationOption = { value: number; label: string };

/**
 * A `CONSULT-{N}-MIN` block. `duration` is `N`: the block's identity, never
 * edited.
 */
export type ConsultationBlock = {
  /**
   * The block's index in `Schedule.extension` when the form was loaded, or
   * undefined for a block created in the form and not saved yet. The save
   * addresses the block by it, and removes a stored block the form no
   * longer lists.
   */
  extensionIndex: number | undefined;
  duration: number;
  alignmentInterval: number;
  buffer: SlotBuffer;
  display: string;
};

/**
 * The fields of one consultation an edit changes. Only those: a debounced
 * edit must not carry the rest of the block, from the render that started
 * it, over later edits. `duration` is the block's identity, never edited.
 */
export type ConsultationEdit = Partial<
  Omit<ConsultationBlock, "duration" | "extensionIndex">
>;

/**
 * Why a duration cannot become a new consultation block
 */
export type ConsultationDurationError = "out-of-range" | "taken";

/**
 * The item that edits the shared hours. `office-visit` also owns the slot
 * duration and buffer; `working-hours` is a schedule where no block owns
 * them.
 */
export type HoursItem = "office-visit" | "working-hours";

export type SchedulingParams = {
  /**
   * The shared hours, copied into every block on save
   */
  weekSchedule: WeekSchedule;
  slotDuration: number;
  buffer: SlotBuffer;
  consultations: ConsultationBlock[];
  hoursItem: HoursItem;
};

/**
 * What the page loads: the form values, plus facts about the stored blocks
 * the form shows but the save never takes.
 */
export type ParsedSchedulingParams = SchedulingParams & {
  /**
   * Some block's stored hours differ from the primary block's, which a save
   * overwrites with the shared hours
   */
  hoursDiffer: boolean;
  /**
   * Blocks a save keeps whatever happens to the consultations: the
   * office-visit block (or the one the first save adds) and blocks outside
   * the edit scope
   */
  otherBlockCount: number;
};

/**
 * The open accordion item: the hours item, or a consultation by its index
 * in `consultations`. Not by duration: two blocks may share one.
 */
export type OpenAvailabilityBlock = HoursItem | number;

/**
 * One calendar's bookable hours, drawn as a lane of the availability rail
 */
export type AvailabilityLane = {
  roleId: string;
  /**
   * Calendar name, shown on hover
   */
  label: string;
  colorClass: string;
  weekSchedule: WeekSchedule;
};

/**
 * The calendar views the app offers. `@ilamy/calendar` types a view as any
 * string, since plugins may register their own, so library values are
 * narrowed with `isCalendarView` before use.
 */
export type CalendarView = "day" | "week" | "month";

export type CalendarColor =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | "cancelled"
  | "busy"
  | "available"
  | "free";

export type ParsedParticipantRef =
  | { resourceType: "Patient"; id: string; ref: string }
  | { resourceType: "PractitionerRole"; id: string; ref: string }
  | { resourceType: "Other"; id: string; ref: string };

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace CalendarEvent {
  export type DataBase = {
    colorClass: string;
    roleColorClass: string;
    calendarName?: string;
    status?: string;
    comment?: string;
  };

  export type SlotData = DataBase & {
    type: "slot";
    slotId: string;
  };

  export type AppointmentData = DataBase & {
    type: "appointment";
    id: string;
    serviceType?: string;
    locationName?: string;
    participantRefs?: string[];
  };

  export type Data = SlotData | AppointmentData;
}
